import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  FaSort,
  FaSortUp,
  FaSortDown,
  FaChevronDown,
  FaCheck
} from 'react-icons/fa';

const DataSort = ({
  options = [],
  sortLabel = 'Sort By',
  sortValue: controlledSortValue,
  defaultSortValue = '',
  sortOrder: controlledSortOrder,
  defaultSortOrder = 'asc',
  onSortChange,
  showSortOrderToggle = true,
  className = ''
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const containerRef = useRef(null);

  const isControlledSort = controlledSortValue !== undefined;
  const [internalSortValue, setInternalSortValue] = useState(defaultSortValue);
  const activeSortValue = isControlledSort ? controlledSortValue : internalSortValue;

  const isControlledSortOrder = controlledSortOrder !== undefined;
  const [internalSortOrder, setInternalSortOrder] = useState(defaultSortOrder);
  const activeSortOrder = isControlledSortOrder ? controlledSortOrder : internalSortOrder;

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const handleSortSelect = (newSortBy) => {
    if (!isControlledSort) {
      setInternalSortValue(newSortBy);
    }
    if (typeof onSortChange === 'function') {
      onSortChange({ by: newSortBy, order: activeSortOrder });
    }
    setIsDropdownOpen(false);
  };

  const handleSortOrderToggle = () => {
    const nextOrder = activeSortOrder === 'asc' ? 'desc' : 'asc';
    if (!isControlledSortOrder) {
      setInternalSortOrder(nextOrder);
    }
    if (typeof onSortChange === 'function') {
      onSortChange({ by: activeSortValue, order: nextOrder });
    }
  };

  if (!options || options.length === 0) return null;

  const currentOption = options.find(
    (opt) => (opt.value !== undefined ? opt.value : opt.key) === activeSortValue
  );
  const currentLabel = currentOption ? (currentOption.label || currentOption.name) : sortLabel;

  return (
    <div className={`relative inline-flex items-center gap-1.5 ${className}`} ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-xs font-semibold transition-colors shadow-2xs focus:outline-none focus:ring-2 focus:ring-[#3B74BE]/20 cursor-pointer ${
          activeSortValue
            ? 'border-[#3B74BE] bg-blue-50 text-[#3B74BE]'
            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
        }`}
      >
        <FaSort className={activeSortValue ? 'text-[#3B74BE]' : 'text-gray-400'} />
        <span>{currentLabel}</span>
        <FaChevronDown
          className={`h-3 w-3 text-gray-400 transition-transform duration-200 ${
            isDropdownOpen ? 'rotate-180 text-[#3B74BE]' : ''
          }`}
        />
      </button>

      {activeSortValue && showSortOrderToggle && (
        <button
          type="button"
          title={activeSortOrder === 'asc' ? 'Ascending Order (Click for Descending)' : 'Descending Order (Click for Ascending)'}
          onClick={handleSortOrderToggle}
          className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white p-2 text-xs text-gray-700 hover:bg-gray-50 shadow-2xs transition-colors cursor-pointer"
        >
          {activeSortOrder === 'asc' ? (
            <FaSortUp className="w-3.5 h-3.5 translate-y-0.5 text-[#3B74BE]" />
          ) : (
            <FaSortDown className="w-3.5 h-3.5 -translate-y-0.5 text-[#3B74BE]" />
          )}
        </button>
      )}

      {isDropdownOpen && (
        <div className="absolute left-0 top-full z-50 mt-1.5 w-48 rounded-md border border-gray-200 bg-white shadow-lg ring-1 ring-black/5">
          <div className="p-2 border-b border-gray-100 bg-gray-50 rounded-t-md flex items-center justify-between text-xs">
            <span className="font-semibold text-gray-700">{sortLabel}</span>
            {activeSortValue && (
              <button
                type="button"
                onClick={() => handleSortSelect('')}
                className="text-gray-500 font-medium hover:text-red-600 hover:underline cursor-pointer"
              >
                Clear
              </button>
            )}
          </div>
          <div className="py-1">
            {options.map((opt) => {
              const val = opt.value !== undefined ? opt.value : opt.key;
              const label = opt.label || opt.name || val;
              const isSelected = activeSortValue === val;

              return (
                <div
                  key={val}
                  onClick={() => handleSortSelect(val)}
                  className={`flex items-center justify-between px-3 py-2 text-xs cursor-pointer transition-colors ${
                    isSelected
                      ? 'bg-blue-50 text-[#3B74BE] font-semibold'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span>{label}</span>
                  {isSelected && <FaCheck className="text-[10px] text-[#3B74BE]" />}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

DataSort.propTypes = {
  options: PropTypes.array,
  sortLabel: PropTypes.string,
  sortValue: PropTypes.string,
  defaultSortValue: PropTypes.string,
  sortOrder: PropTypes.oneOf(['asc', 'desc']),
  defaultSortOrder: PropTypes.oneOf(['asc', 'desc']),
  onSortChange: PropTypes.func,
  showSortOrderToggle: PropTypes.bool,
  className: PropTypes.string
};

export default DataSort;
